import React from "react"
import { View } from "react-native"
import { Body, H1, H2, Small } from "@/components/standard/BFText"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { Badge } from "@/components/reusables/ui/badge"
import { Card, CardContent } from "@/components/reusables/ui/card"
import { Button } from "@/components/reusables/ui/button"
import { useUpdateCompSelectionMutation } from "@/queries/analysis"

interface AnalysisHeaderProps {
  loanApplication: {
    property?: {
      address?: string
      area?: number
    }
    purchasePrice?: number
    afterRepairValue?: number
    rehabEstimate?: number
    costBasis?: number
    grossProfit?: number
  }
  analysis?: {
    id?: string
    purchasePrice?: number | null
    afterRepairValue?: number | null
    rehabCost?: number | null
    grossProfitPotential?: number | null
    property?: {
      area?: number | null
      latitude?: string | null
      longitude?: string | null
    }
    strategy?: string | null
    returnOnInvestment?: number | null
    comps?: any[] | null
  }
  ownScenarioComps?: Set<string>
}

export function AnalysisHeader({
  loanApplication,
  analysis,
  ownScenarioComps = new Set(),
}: AnalysisHeaderProps) {
  const propertyAddress = loanApplication?.property?.address
  const propertyArea =
    loanApplication?.property?.area || analysis?.property?.area
  const analysisStatus = "Draft"

  const updateCompSelection = useUpdateCompSelectionMutation()

  const handleRecalculateAnalysis = () => {
    if (!analysis?.id || !analysis?.comps) return

    // Get all currently selected comp IDs
    const selectedCompIds = analysis.comps
      .filter((comp: any) => comp?.isSelected)
      .map((comp: any) => comp?.arvcomp?.id)
      .filter(Boolean)

    // Trigger recalculation by updating comp selections (even with same IDs)
    updateCompSelection.mutate({
      analysisId: analysis.id,
      selectedCompIds,
    })
  }

  const formatCurrency = (value?: number | null) => {
    if (!value) return "—"
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatPerSqFt = (value?: number | null, area?: number | null) => {
    if (!value || !area) return "—"
    const perSqFt = value / area
    return `$${Math.round(perSqFt)}/sq ft`
  }

  // Compare loan vs analysis values
  const loanPurchasePrice = loanApplication?.purchasePrice
  const analysisPurchasePrice = analysis?.purchasePrice

  const loanARV = loanApplication?.afterRepairValue
  const analysisARV = analysis?.afterRepairValue

  const loanRehabCost = loanApplication?.rehabEstimate
  const analysisRehabCost = analysis?.rehabCost

  const loanGrossProfit = loanApplication?.grossProfit
  const analysisGrossProfit = analysis?.grossProfitPotential

  // Helper functions for comp calculations
  const getSelectedComps = (comps: any[]) => {
    return comps.filter((comp: any) => comp?.isSelected && comp?.arvcomp)
  }

  const getClosestComps = (comps: any[], count: number) => {
    const validComps = comps.filter(
      (comp: any) => comp?.arvcomp?.latitude && comp?.arvcomp?.longitude
    )

    if (!analysis?.property?.latitude || !analysis?.property?.longitude) {
      return validComps.slice(0, count) // fallback to first comps if no property coords
    }

    const propertyLat = parseFloat(analysis.property.latitude)
    const propertyLng = parseFloat(analysis.property.longitude)

    return validComps
      .map((comp: any) => ({
        ...comp,
        distance: calculateDistance(
          propertyLat,
          propertyLng,
          comp.arvcomp.latitude as number,
          comp.arvcomp.longitude as number
        ),
      }))
      .sort((a: any, b: any) => a.distance - b.distance)
      .slice(0, count)
  }

  const getLowestPriceComps = (comps: any[], count: number) => {
    return comps
      .filter((comp: any) => comp?.arvcomp?.pricePerSqft)
      .sort((a: any, b: any) => a.arvcomp.pricePerSqft - b.arvcomp.pricePerSqft)
      .slice(0, count)
  }

  const getHighestPriceComps = (comps: any[], count: number) => {
    return comps
      .filter((comp: any) => comp?.arvcomp?.pricePerSqft)
      .sort((a: any, b: any) => b.arvcomp.pricePerSqft - a.arvcomp.pricePerSqft)
      .slice(0, count)
  }

  const getOwnScenarioComps = (comps: any[]) => {
    return comps.filter(
      (comp: any) =>
        comp?.arvcomp?.id && ownScenarioComps.has(comp.arvcomp.id as string)
    )
  }

  const calculateDistance = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ) => {
    const R = 3959 // Earth's radius in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLng = ((lng2 - lng1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const calculateARV = (comps: any[]) => {
    if (comps.length === 0) return 0
    const validPrices = comps
      .map((comp: any) => {
        if (comp?.arvcomp?.pricing) {
          try {
            const pricing = JSON.parse(comp.arvcomp.pricing as string)
            return pricing.price
          } catch {
            return null
          }
        }
        return null
      })
      .filter((price: any): price is number => typeof price === "number")

    if (validPrices.length === 0) return 0
    return (
      validPrices.reduce((sum: number, price: number) => sum + price, 0) /
      validPrices.length
    )
  }

  const calculateAvgPricePerSqFt = (comps: any[]) => {
    if (comps.length === 0) return 0
    const validPrices = comps
      .map((comp: any) => comp?.arvcomp?.pricePerSqft)
      .filter((price: any): price is number => typeof price === "number")

    if (validPrices.length === 0) return 0
    const avg =
      validPrices.reduce((sum: number, price: number) => sum + price, 0) /
      validPrices.length
    return Math.round(avg)
  }

  const calculateAvgSqFt = (comps: any[]) => {
    if (comps.length === 0) return 0
    const validAreas = comps
      .map((comp: any) => comp?.arvcomp?.area)
      .filter((area: any): area is number => typeof area === "number")

    if (validAreas.length === 0) return 0
    return Math.round(
      validAreas.reduce((sum: number, area: number) => sum + area, 0) /
        validAreas.length
    )
  }

  const getDifference = (loanValue?: number, analysisValue?: number | null) => {
    if (!loanValue || !analysisValue) return null
    const diff = analysisValue - loanValue
    return diff
  }

  const getDifferenceDisplay = (diff?: number | null) => {
    if (diff === null || diff === undefined) return ""
    const sign = diff >= 0 ? "+" : ""
    return ` (${sign}${formatCurrency(diff)})`
  }

  return (
    <View className="bg-card border-b border-border px-5 py-6">
      <Vertical className="gap-6">
        {/* Title and Status */}
        <Horizontal className="items-center justify-between">
          <H1 className="text-foreground flex-1">Loan Analysis Comparison</H1>

          <Horizontal className="gap-3 items-center">
            <Button
              variant="outline"
              size="sm"
              onPress={handleRecalculateAnalysis}
              disabled={updateCompSelection.isPending}
              testID="recalculate-analysis"
            >
              <Small className="font-medium">
                {updateCompSelection.isPending
                  ? "Recalculating..."
                  : "Recalculate Analysis"}
              </Small>
            </Button>

            <Badge variant="secondary">
              <Small className="text-xs font-medium">{analysisStatus}</Small>
            </Badge>
          </Horizontal>
        </Horizontal>

        {/* Property Address */}
        {propertyAddress && (
          <View>
            <H2 className="text-foreground">{propertyAddress}</H2>
            {propertyArea && (
              <Small className="text-muted-foreground mt-1">
                {propertyArea.toLocaleString()} sq ft
              </Small>
            )}
          </View>
        )}

        {/* Compact Comparison Table */}
        <Card>
          <CardContent className="p-4">
            <Vertical className="gap-3">
              {/* Table Header */}
              <Horizontal className="border-b border-border pb-2">
                <View className="flex-1">
                  <Small className="text-muted-foreground font-medium">
                    Metric
                  </Small>
                </View>
                <View className="flex-1 items-center">
                  <Small className="text-muted-foreground font-medium">
                    Loan App
                  </Small>
                </View>
                <View className="flex-1 items-center">
                  <Small className="text-muted-foreground font-medium">
                    Analysis
                  </Small>
                </View>
                <View className="flex-1 items-center">
                  <Small className="text-muted-foreground font-medium">
                    Difference
                  </Small>
                </View>
              </Horizontal>

              {/* Purchase Price Row */}
              <Horizontal className="py-1">
                <View className="flex-1">
                  <Body className="font-medium">Purchase Price</Body>
                </View>
                <View className="flex-1 items-center">
                  <Body className="text-center">
                    {formatCurrency(loanPurchasePrice)}
                  </Body>
                  {propertyArea && (
                    <Small className="text-muted-foreground text-center">
                      {formatPerSqFt(loanPurchasePrice, propertyArea)}
                    </Small>
                  )}
                </View>
                <View className="flex-1 items-center">
                  <Body className="text-center">
                    {formatCurrency(analysisPurchasePrice)}
                  </Body>
                  {propertyArea && (
                    <Small className="text-muted-foreground text-center">
                      {formatPerSqFt(analysisPurchasePrice, propertyArea)}
                    </Small>
                  )}
                </View>
                <View className="flex-1 items-center">
                  <Small
                    className={`text-center ${getDifference(loanPurchasePrice, analysisPurchasePrice) && getDifference(loanPurchasePrice, analysisPurchasePrice)! > 0 ? "text-green-700" : getDifference(loanPurchasePrice, analysisPurchasePrice) && getDifference(loanPurchasePrice, analysisPurchasePrice)! < 0 ? "text-destructive" : "text-muted-foreground"}`}
                  >
                    {getDifferenceDisplay(
                      getDifference(loanPurchasePrice, analysisPurchasePrice)
                    ) || "—"}
                  </Small>
                </View>
              </Horizontal>

              {/* ARV Row */}
              <Horizontal className="py-1">
                <View className="flex-1">
                  <Body className="font-medium">ARV</Body>
                </View>
                <View className="flex-1 items-center">
                  <Body className="text-center">{formatCurrency(loanARV)}</Body>
                  {propertyArea && (
                    <Small className="text-muted-foreground text-center">
                      {formatPerSqFt(loanARV, propertyArea)}
                    </Small>
                  )}
                </View>
                <View className="flex-1 items-center">
                  <Body className="text-center">
                    {formatCurrency(analysisARV)}
                  </Body>
                  {propertyArea && (
                    <Small className="text-muted-foreground text-center">
                      {formatPerSqFt(analysisARV, propertyArea)}
                    </Small>
                  )}
                </View>
                <View className="flex-1 items-center">
                  <Small
                    className={`text-center ${getDifference(loanARV, analysisARV) && getDifference(loanARV, analysisARV)! > 0 ? "text-green-700" : getDifference(loanARV, analysisARV) && getDifference(loanARV, analysisARV)! < 0 ? "text-destructive" : "text-muted-foreground"}`}
                  >
                    {getDifferenceDisplay(
                      getDifference(loanARV, analysisARV)
                    ) || "—"}
                  </Small>
                </View>
              </Horizontal>

              {/* Rehab Cost Row */}
              <Horizontal className="py-1">
                <View className="flex-1">
                  <Body className="font-medium">Rehab Cost</Body>
                </View>
                <View className="flex-1 items-center">
                  <Body className="text-center">
                    {formatCurrency(loanRehabCost)}
                  </Body>
                  {propertyArea && (
                    <Small className="text-muted-foreground text-center">
                      {formatPerSqFt(loanRehabCost, propertyArea)}
                    </Small>
                  )}
                </View>
                <View className="flex-1 items-center">
                  <Body className="text-center">
                    {formatCurrency(analysisRehabCost)}
                  </Body>
                  {propertyArea && (
                    <Small className="text-muted-foreground text-center">
                      {formatPerSqFt(analysisRehabCost, propertyArea)}
                    </Small>
                  )}
                </View>
                <View className="flex-1 items-center">
                  <Small
                    className={`text-center ${getDifference(loanRehabCost, analysisRehabCost) && getDifference(loanRehabCost, analysisRehabCost)! > 0 ? "text-destructive" : getDifference(loanRehabCost, analysisRehabCost) && getDifference(loanRehabCost, analysisRehabCost)! < 0 ? "text-green-700" : "text-muted-foreground"}`}
                  >
                    {getDifferenceDisplay(
                      getDifference(loanRehabCost, analysisRehabCost)
                    ) || "—"}
                  </Small>
                </View>
              </Horizontal>

              {/* Gross Profit Row */}
              <Horizontal className="py-1">
                <View className="flex-1">
                  <Body className="font-medium">Gross Profit</Body>
                </View>
                <View className="flex-1 items-center">
                  <Body className="text-center">
                    {formatCurrency(loanGrossProfit)}
                  </Body>
                </View>
                <View className="flex-1 items-center">
                  <Body className="text-center">
                    {formatCurrency(analysisGrossProfit)}
                  </Body>
                </View>
                <View className="flex-1 items-center">
                  <Small
                    className={`text-center ${getDifference(loanGrossProfit, analysisGrossProfit) && getDifference(loanGrossProfit, analysisGrossProfit)! > 0 ? "text-green-700" : getDifference(loanGrossProfit, analysisGrossProfit) && getDifference(loanGrossProfit, analysisGrossProfit)! < 0 ? "text-destructive" : "text-muted-foreground"}`}
                  >
                    {getDifferenceDisplay(
                      getDifference(loanGrossProfit, analysisGrossProfit)
                    ) || "—"}
                  </Small>
                </View>
              </Horizontal>
            </Vertical>
          </CardContent>
        </Card>

        {/* Comps Analysis Table */}
        {analysis?.comps && (
          <Card>
            <CardContent className="p-4">
              <Vertical className="gap-3">
                {/* Table Title */}
                <H2 className="text-foreground">Comps Analysis Comparison</H2>

                {/* Table Header */}
                <Horizontal className="border-b border-border pb-2">
                  <View className="flex-1">
                    <Small className="text-muted-foreground font-medium">
                      Metric
                    </Small>
                  </View>
                  <View className="flex-1 items-center">
                    <Small className="text-muted-foreground font-medium text-center">
                      Selected
                    </Small>
                    <Small className="text-muted-foreground text-center">
                      ({getSelectedComps(analysis.comps).length})
                    </Small>
                  </View>
                  <View className="flex-1 items-center">
                    <Small className="text-muted-foreground font-medium text-center">
                      3 Closest
                    </Small>
                    <Small className="text-muted-foreground text-center">
                      (distance)
                    </Small>
                  </View>
                  <View className="flex-1 items-center">
                    <Small className="text-muted-foreground font-medium text-center">
                      3 Lowest
                    </Small>
                    <Small className="text-muted-foreground text-center">
                      ($/sq ft)
                    </Small>
                  </View>
                  <View className="flex-1 items-center">
                    <Small className="text-muted-foreground font-medium text-center">
                      3 Highest
                    </Small>
                    <Small className="text-muted-foreground text-center">
                      ($/sq ft)
                    </Small>
                  </View>
                  <View className="flex-1 items-center">
                    <Small className="text-muted-foreground font-medium text-center">
                      Own Scenario
                    </Small>
                    <Small className="text-muted-foreground text-center">
                      ({getOwnScenarioComps(analysis.comps).length})
                    </Small>
                  </View>
                </Horizontal>

                {/* ARV Row */}
                <Horizontal className="py-1">
                  <View className="flex-1">
                    <Body className="font-medium">ARV</Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {formatCurrency(
                        calculateARV(getSelectedComps(analysis.comps))
                      )}
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {formatCurrency(
                        calculateARV(getClosestComps(analysis.comps, 3))
                      )}
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {formatCurrency(
                        calculateARV(getLowestPriceComps(analysis.comps, 3))
                      )}
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {formatCurrency(
                        calculateARV(getHighestPriceComps(analysis.comps, 3))
                      )}
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {formatCurrency(
                        calculateARV(getOwnScenarioComps(analysis.comps))
                      )}
                    </Body>
                  </View>
                </Horizontal>

                {/* Price/Sq.Ft Row */}
                <Horizontal className="py-1">
                  <View className="flex-1">
                    <Body className="font-medium">Price/Sq.Ft</Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      $
                      {calculateAvgPricePerSqFt(
                        getSelectedComps(analysis.comps)
                      )}
                      /sq ft
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      $
                      {calculateAvgPricePerSqFt(
                        getClosestComps(analysis.comps, 3)
                      )}
                      /sq ft
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      $
                      {calculateAvgPricePerSqFt(
                        getLowestPriceComps(analysis.comps, 3)
                      )}
                      /sq ft
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      $
                      {calculateAvgPricePerSqFt(
                        getHighestPriceComps(analysis.comps, 3)
                      )}
                      /sq ft
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      $
                      {calculateAvgPricePerSqFt(
                        getOwnScenarioComps(analysis.comps)
                      )}
                      /sq ft
                    </Body>
                  </View>
                </Horizontal>

                {/* Avg Sq.Ft Row */}
                <Horizontal className="py-1">
                  <View className="flex-1">
                    <Body className="font-medium">Avg Sq.Ft</Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {calculateAvgSqFt(
                        getSelectedComps(analysis.comps)
                      ).toLocaleString()}
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {calculateAvgSqFt(
                        getClosestComps(analysis.comps, 3)
                      ).toLocaleString()}
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {calculateAvgSqFt(
                        getLowestPriceComps(analysis.comps, 3)
                      ).toLocaleString()}
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {calculateAvgSqFt(
                        getHighestPriceComps(analysis.comps, 3)
                      ).toLocaleString()}
                    </Body>
                  </View>
                  <View className="flex-1 items-center">
                    <Body className="text-center">
                      {calculateAvgSqFt(
                        getOwnScenarioComps(analysis.comps)
                      ).toLocaleString()}
                    </Body>
                  </View>
                </Horizontal>
              </Vertical>
            </CardContent>
          </Card>
        )}
      </Vertical>
    </View>
  )
}
